// P10.1 hotfix — heal SAVED structural strategies compiled by the defective P9.2 entry
// bridge.
//
// Defect: the compiler emitted `EXPRESSION: machine-value > 0 → ENTRY`, but expressions
// compare the machine's STATE-INDEX values and completion resets the state within the same
// bar — so 2-state machines never fired an entry and deeper machines fired on ARMING
// rather than completion. The corrected compiler wires ENTRY directly to the machine's
// COMPLETION SIGNALS (terminalFires ORs input signals).
//
// This heal rewrites any PERSISTED graph carrying the broken pattern to the corrected
// wiring: entry.inputs ← [machine], the bridging expression is removed, and the machine's
// outputs are repointed. Idempotent; graphs without the exact pattern are returned
// unchanged (legacy strategies are untouched — they have no setup_state block).

import { BlockType, type Block } from '../../types';

export function healStructuralEntryBridge(blocks: Block[]): Block[] {
  const machine = blocks.find((b) => b.type === BlockType.SETUP_STATE);
  if (!machine) return blocks; // not a structural strategy — never touched

  // the broken bridge: EXPRESSION whose left input is the machine, comparing '>' 0
  const bridge = blocks.find((b) => {
    if (b.type !== BlockType.EXPRESSION) return false;
    const d = b.data as unknown as Record<string, unknown>;
    return d.leftInputBlockId === machine.id && d.operator === '>' &&
      d.rightSource === 'value' && Number(d.rightValue) === 0 &&
      (b.connections?.inputs ?? []).includes(machine.id);
  });
  if (!bridge) return blocks; // already corrected (or hand-built) — unchanged

  const entryIds = new Set(
    blocks.filter((b) => b.type === BlockType.ENTRY && (b.connections?.inputs ?? []).includes(bridge.id)).map((b) => b.id),
  );
  if (entryIds.size === 0) return blocks; // pattern incomplete — do not guess

  return blocks
    .filter((b) => b.id !== bridge.id)
    .map((b) => {
      if (b.id === machine.id) {
        const outputs = (b.connections?.outputs ?? []).filter((o) => o !== bridge.id);
        for (const e of entryIds) if (!outputs.includes(e)) outputs.push(e);
        return { ...b, connections: { inputs: b.connections?.inputs ?? [], outputs } };
      }
      if (entryIds.has(b.id)) {
        const inputs = (b.connections?.inputs ?? []).filter((x) => x !== bridge.id);
        if (!inputs.includes(machine.id)) inputs.push(machine.id);
        return { ...b, connections: { inputs, outputs: b.connections?.outputs ?? [] } };
      }
      return b;
    });
}
